import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";

const API_TRAVEL =
  process.env.REACT_APP_BACKEND_URL + "/api/v1/travel/accommodations";

const BOOKINGS_ENDPOINT = `${API_TRAVEL}/accommodation-bookings`;
const cardsBorderRadii = "8px";

type BookingStatus =
  | "confirmed"
  | "cancelled"
  | "pending"
  | "checked_in"
  | "checked_out";

type AccommodationBooking = {
  _id: string;
  accommodationId: string;
  roomId: string;
  userId?: string;
  orderId?: string;
  checkIn: string;
  checkOut: string;
  roomsBooked?: number;
  guests?: number;
  status: BookingStatus;
  createdAt?: string;
  updatedAt?: string;
  checkedInAt?: string;
  checkedOutAt?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  paymentProvider?: string;
  paymentReference?: string;
  customerName?: string;
  guestName?: string;
  customerPhone?: string;
  guestPhone?: string;
  guestEmail?: string;
  paymentLink?: string;
  invoiceLink?: string;
  trackingLink?: string;
  checkInLink?: string;
  metadata?: {
    propertyName?: string;
    roomTitle?: string;
    location?: string;
    customerName?: string;
    customerPhone?: string;
    ownerName?: string;
    checkIn?: string;
    checkOut?: string;
    nights?: number;
    guests?: number;
    invoiceLink?: string;
    paymentLink?: string;
    trackingLink?: string;
    checkInLink?: string;
  };
};

function statusColor(status?: BookingStatus) {
  switch (status) {
    case "confirmed":
      return "green";
    case "pending":
      return "yellow";
    case "cancelled":
      return "red";
    case "checked_in":
      return "blue";
    case "checked_out":
      return "purple";
    default:
      return "gray";
  }
}

function formatDate(value?: string) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(+date)) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(value?: string) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(+date)) {
    return "—";
  }

  return date.toLocaleString();
}

function nightsBetween(checkIn?: string, checkOut?: string) {
  if (!checkIn || !checkOut) return 0;

  const start = new Date(checkIn);
  const end = new Date(checkOut);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diff = Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );

  return Math.max(0, diff);
}

function getBookingPayload(responseData: any) {
  return responseData?.data || responseData?.booking || responseData;
}

function getPropertyName(booking: AccommodationBooking) {
  return (
    booking.metadata?.propertyName ||
    booking.metadata?.ownerName ||
    "Accommodation booking"
  );
}

function getRoomTitle(booking: AccommodationBooking) {
  return booking.metadata?.roomTitle || booking.roomId || "Selected room";
}

function getGuestName(booking: AccommodationBooking) {
  return (
    booking.customerName ||
    booking.guestName ||
    booking.metadata?.customerName ||
    "Guest"
  );
}

function getGuestPhone(booking: AccommodationBooking) {
  return (
    booking.customerPhone ||
    booking.guestPhone ||
    booking.metadata?.customerPhone ||
    "—"
  );
}

function canCheckIn(booking: AccommodationBooking) {
  return (
    booking.status !== "cancelled" &&
    booking.status !== "checked_in" &&
    booking.status !== "checked_out" &&
    !booking.checkedInAt
  );
}

function canCheckOut(booking: AccommodationBooking) {
  return (
    booking.status !== "cancelled" &&
    booking.status !== "checked_out" &&
    Boolean(booking.checkedInAt || booking.status === "checked_in") &&
    !booking.checkedOutAt
  );
}

export default function AccommodationGuestCheckInPage() {
  const { bookingId } = useParams();
  const { theme } = useCustomTheme();
  const toast = useToast();
  const navigate = useNavigate();

  const [booking, setBooking] = useState<AccommodationBooking | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<
    "check-in" | "check-out" | null
  >(null);

  const nights = useMemo(
    () => nightsBetween(booking?.checkIn, booking?.checkOut),
    [booking?.checkIn, booking?.checkOut],
  );

  const fetchBooking = async () => {
    if (!bookingId) return;

    setLoading(true);

    try {
      const response = await axios.get(`${BOOKINGS_ENDPOINT}/${bookingId}`);
      setBooking(getBookingPayload(response.data));
    } catch (error: any) {
      console.error("Failed to load accommodation booking:", error);

      toast({
        title: "Booking not found",
        description:
          error?.response?.data?.message ||
          "We could not load this accommodation booking.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const handleCheckIn = async () => {
    if (!booking?._id) return;

    setActionLoading("check-in");

    try {
      const response = await axios.patch(
        `${BOOKINGS_ENDPOINT}/${booking._id}/check-in`,
        {},
      );

      const updatedBooking = getBookingPayload(response.data);
      setBooking(updatedBooking);

      toast({
        title: "Checked in successfully",
        description: "Your stay has been marked as checked in.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Accommodation check-in failed:", error);

      toast({
        title: "Check-in failed",
        description:
          error?.response?.data?.message ||
          "Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCheckOut = async () => {
    if (!booking?._id) return;

    setActionLoading("check-out");

    try {
      const response = await axios.patch(
        `${BOOKINGS_ENDPOINT}/${booking._id}/check-out`,
        {},
      );

      const updatedBooking = getBookingPayload(response.data);
      setBooking(updatedBooking);

      toast({
        title: "Checked out successfully",
        description: "Your stay has been marked as checked out.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Accommodation check-out failed:", error);

      toast({
        title: "Check-out failed",
        description:
          error?.response?.data?.message ||
          "Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Box
        minH="100vh"
        bg={theme.surface}
        color={theme.text}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={6}
      >
        <Stack align="center" spacing={4}>
          <GlobalSpinner />
          <Text color={theme.textSubtle}>Loading your booking...</Text>
        </Stack>
      </Box>
    );
  }

  if (!booking) {
    return (
      <Box
        minH="100vh"
        bg={theme.surface}
        color={theme.text}
        p={{ base: 4, md: 8 }}
      >
        <Box
          maxW="720px"
          mx="auto"
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={{ base: 5, md: 8 }}
          boxShadow={theme.shadowSm}
        >
          <Alert status="error" borderRadius={cardsBorderRadii}>
            <AlertIcon />
            Booking not found or no longer available.
          </Alert>

          <Button
            mt={5}
            onClick={() => navigate("/book/accommodation")}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
          >
            Browse Accommodation
          </Button>
        </Box>
      </Box>
    );
  }

  const showCheckIn = canCheckIn(booking);
  const showCheckOut = canCheckOut(booking);
  const isCancelled = booking.status === "cancelled";
  const isCheckedOut = booking.status === "checked_out" || booking.checkedOutAt;
  const isCheckedIn = booking.status === "checked_in" || booking.checkedInAt;

  return (
    <Box
      minH="100vh"
      bg={theme.surface}
      color={theme.text}
      p={{ base: 4, md: 8 }}
    >
      <Box maxW="960px" mx="auto">
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          overflow="hidden"
          boxShadow={theme.shadowMd || theme.shadowSm}
        >
          <Box
            p={{ base: 5, md: 8 }}
            bg={theme.surfaceAlt}
            borderBottom="1px solid"
            borderColor={theme.border}
          >
            <Flex
              align={{ base: "start", md: "center" }}
              justify="space-between"
              gap={4}
              wrap="wrap"
            >
              <Box>
                <Badge
                  colorScheme={statusColor(booking.status)}
                  borderRadius={cardsBorderRadii}
                  px={3}
                  py={1}
                  textTransform="capitalize"
                >
                  {(booking.status || "pending").replace("_", " ")}
                </Badge>

                <Heading mt={3} size="lg" color={theme.text}>
                  {getPropertyName(booking)}
                </Heading>

                <Text mt={2} color={theme.textSubtle}>
                  Check-in and check-out for your accommodation booking.
                </Text>
              </Box>

              <Stack align={{ base: "stretch", md: "flex-end" }}>
                <Text fontSize="sm" color={theme.textSubtle}>
                  Booking ID
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={theme.text}
                  wordBreak="break-all"
                >
                  {booking._id}
                </Text>
              </Stack>
            </Flex>
          </Box>

          <Box p={{ base: 5, md: 8 }}>
            {isCancelled && (
              <Alert status="error" borderRadius={cardsBorderRadii} mb={5}>
                <AlertIcon />
                This booking has been cancelled. Check-in is not available.
              </Alert>
            )}

            {isCheckedOut && (
              <Alert status="success" borderRadius={cardsBorderRadii} mb={5}>
                <AlertIcon />
                You have already checked out. Thank you for using PickDengu.
              </Alert>
            )}

            {isCheckedIn && !isCheckedOut && (
              <Alert status="info" borderRadius={cardsBorderRadii} mb={5}>
                <AlertIcon />
                You are already checked in. You can check out when your stay is
                complete.
              </Alert>
            )}

            {!isCheckedIn && !isCancelled && (
              <Alert status="warning" borderRadius={cardsBorderRadii} mb={5}>
                <AlertIcon />
                Please confirm your details before checking in.
              </Alert>
            )}

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <DetailCard
                label="Guest"
                value={getGuestName(booking)}
                subValue={getGuestPhone(booking)}
              />

              <DetailCard
                label="Room"
                value={getRoomTitle(booking)}
                subValue={`Guests: ${booking.guests ?? 1}`}
              />

              <DetailCard
                label="Check-in"
                value={formatDate(booking.checkIn)}
                subValue={
                  booking.checkedInAt
                    ? `Checked in at ${formatDateTime(booking.checkedInAt)}`
                    : "Not checked in yet"
                }
              />

              <DetailCard
                label="Check-out"
                value={formatDate(booking.checkOut)}
                subValue={
                  booking.checkedOutAt
                    ? `Checked out at ${formatDateTime(booking.checkedOutAt)}`
                    : "Not checked out yet"
                }
              />

              <DetailCard
                label="Stay"
                value={`${nights} night${nights === 1 ? "" : "s"}`}
                subValue={`Rooms booked: ${booking.roomsBooked ?? 1}`}
              />

              <DetailCard
                label="Payment"
                value={booking.paymentStatus || "pending"}
                subValue={
                  booking.orderId
                    ? `Order: ${booking.orderId}`
                    : "Order reference unavailable"
                }
              />
            </SimpleGrid>

            <Divider my={6} borderColor={theme.border} />

            <Stack spacing={3}>
              {showCheckIn && (
                <Button
                  size="lg"
                  colorScheme="green"
                  isLoading={actionLoading === "check-in"}
                  onClick={handleCheckIn}
                >
                  Check In
                </Button>
              )}

              {showCheckOut && (
                <Button
                  size="lg"
                  colorScheme="purple"
                  isLoading={actionLoading === "check-out"}
                  onClick={handleCheckOut}
                >
                  Check Out
                </Button>
              )}

              {!showCheckIn && !showCheckOut && (
                <Alert status="info" borderRadius={cardsBorderRadii}>
                  <AlertIcon />
                  No check-in or check-out action is currently available for
                  this booking.
                </Alert>
              )}

              <Button
                variant="outline"
                borderColor={theme.borderStrong}
                color={theme.text}
                _hover={{ bg: theme.hover }}
                onClick={fetchBooking}
                isLoading={loading}
              >
                Refresh Booking
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function DetailCard({
  label,
  value,
  subValue,
}: {
  label: string;
  value: string;
  subValue?: string;
}) {
  const { theme } = useCustomTheme();

  return (
    <Box
      border="1px solid"
      borderColor={theme.border}
      bg={theme.surface}
      borderRadius={cardsBorderRadii}
      p={4}
      boxShadow={theme.shadowSm}
    >
      <Text fontSize="xs" color={theme.textSubtle} textTransform="uppercase">
        {label}
      </Text>

      <Text mt={1} fontSize="lg" fontWeight="bold" color={theme.text}>
        {value || "—"}
      </Text>

      {subValue && (
        <Text mt={1} fontSize="sm" color={theme.textMuted}>
          {subValue}
        </Text>
      )}
    </Box>
  );
}
